import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { StickyNote, Trash2, Edit, Save, X, User, Calendar, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const NotesManagement = ({ user }) => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'read', 'unread'

  useEffect(() => {
    if (user.role !== 'admin') {
      toast.error('Access denied. Admin only.');
      navigate('/dashboard');
      return;
    }
    fetchAllNotes();
  }, [user.role, navigate]);

  const fetchAllNotes = async () => {
    setLoading(true);
    try {
      // Fetch all user notes (admin can see all)
      const response = await axios.get(`${API}/user-notes`);
      setNotes(response.data);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/user-notes/${noteId}`);
      toast.success('Note deleted successfully');
      fetchAllNotes();
    } catch (error) {
      toast.error('Failed to delete note');
      console.error('Error deleting note:', error);
    }
  };

  const startEditing = (note) => {
    setEditingNoteId(note.id);
    setEditContent(note.content);
  };

  const cancelEditing = () => {
    setEditingNoteId(null);
    setEditContent('');
  };

  const handleUpdateNote = async (noteId) => {
    if (!editContent.trim()) {
      toast.error('Note content cannot be empty');
      return;
    }

    try {
      await axios.put(`${API}/user-notes/${noteId}`, {
        content: editContent
      });
      toast.success('Note updated successfully');
      setEditingNoteId(null);
      setEditContent('');
      fetchAllNotes();
    } catch (error) {
      toast.error('Failed to update note');
      console.error('Error updating note:', error);
    }
  };

  // Filter and search notes
  const filteredNotes = notes.filter(note => {
    const matchesSearch = 
      note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.created_by_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      filterStatus === 'all' ? true :
      filterStatus === 'read' ? note.is_read :
      filterStatus === 'unread' ? !note.is_read : true;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20 pt-20 flex items-center justify-center">
        <p className="text-gray-600">Loading notes...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pb-20 pt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-orange-500/20 rounded-lg">
              <StickyNote className="w-8 h-8 text-orange-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Notes Management</h1>
              <p className="text-slate-400">View, edit, and delete all notes sent to clients</p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search notes by content or recipient..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setFilterStatus('all')}
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                className={filterStatus === 'all' ? 'bg-orange-500' : ''}
              >
                All ({notes.length})
              </Button>
              <Button
                onClick={() => setFilterStatus('unread')}
                variant={filterStatus === 'unread' ? 'default' : 'outline'}
                className={filterStatus === 'unread' ? 'bg-orange-500' : ''}
              >
                Unread ({notes.filter(n => !n.is_read).length})
              </Button>
              <Button
                onClick={() => setFilterStatus('read')}
                variant={filterStatus === 'read' ? 'default' : 'outline'}
                className={filterStatus === 'read' ? 'bg-orange-500' : ''}
              >
                Read ({notes.filter(n => n.is_read).length})
              </Button>
            </div>
          </div>
        </div>

        {/* Notes List */}
        {filteredNotes.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <StickyNote className="w-16 h-16 mx-auto mb-4 text-slate-600" />
            <p className="text-slate-400 text-lg">
              {searchTerm || filterStatus !== 'all' 
                ? 'No notes match your search criteria' 
                : 'No notes have been sent yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredNotes.map((note) => (
              <div
                key={note.id}
                className={`glass-card p-6 border-2 ${
                  note.is_read
                    ? 'border-slate-700'
                    : 'border-orange-500/50 bg-orange-500/5'
                }`}
              >
                {/* Note Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-white font-semibold">
                        To: {note.recipient_name || 'All Clients'}
                      </span>
                      {!note.is_read && (
                        <span className="px-2 py-1 bg-orange-500 text-white text-xs font-semibold rounded">
                          Unread
                        </span>
                      )}
                      {note.is_read && (
                        <span className="flex items-center gap-1 text-green-400 text-xs">
                          <CheckCircle className="w-3 h-3" />
                          Read
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <span>From: {note.created_by_name}</span>
                      <span>•</span>
                      <Calendar className="w-3 h-3" />
                      <span>
                        {new Date(note.created_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })} at{' '}
                        {new Date(note.created_at).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto' })}
                      </span>
                    </div>
                    {note.is_read && note.read_at && (
                      <div className="text-xs text-slate-500 mt-1">
                        Read on: {new Date(note.read_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })} at{' '}
                        {new Date(note.read_at).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto' })}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {editingNoteId !== note.id && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEditing(note)}
                        className="border-blue-600 text-blue-400 hover:bg-blue-600 hover:text-white"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteNote(note.id)}
                        className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  )}
                </div>

                {/* Note Content */}
                {editingNoteId === note.id ? (
                  <div className="space-y-3">
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="min-h-[100px] bg-slate-800 border-slate-700 text-white"
                      placeholder="Note content..."
                    />
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={cancelEditing}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleUpdateNote(note.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-white whitespace-pre-wrap">{note.content}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesManagement;
